export const TAX_RATE = 0.1025;
export const CARD_FEE_RATE = 0.03;

export const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const roundCurrency = (value) => {
  const number = toFiniteNumber(value);
  const rounded =
    (Math.sign(number) || 1) *
    (Math.round((Math.abs(number) + Number.EPSILON) * 100) / 100);
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const toNonNegativeCurrency = (value) =>
  roundCurrency(Math.max(0, toFiniteNumber(value)));

export const formatCurrency = (value) => {
  const rounded = roundCurrency(value);
  return rounded < 0
    ? `-$${Math.abs(rounded).toFixed(2)}`
    : `$${rounded.toFixed(2)}`;
};

export const isCardPaymentMethod = (paymentMethod) =>
  String(paymentMethod || "")
    .trim()
    .toLowerCase()
    .includes("card");

export const getTaxSettingForPaymentMethod = (
  paymentMethod,
  currentSetting = false,
  wasManuallySet = false
) =>
  wasManuallySet
    ? Boolean(currentSetting)
    : isCardPaymentMethod(paymentMethod);

// Shared booking-charge order used by invoices and deposit confirmations:
// base + additions -> tax -> discount. New offerings have no service fee.
export const calculateBookingCharges = ({
  baseSubtotal = 0,
  additionalCharges = 0,
  discount = 0,
  applyTax = true,
} = {}) => {
  const safeBaseSubtotal = toNonNegativeCurrency(baseSubtotal);
  const safeAdditionalCharges = toNonNegativeCurrency(additionalCharges);
  const safeDiscount = toNonNegativeCurrency(discount);
  const subtotal = roundCurrency(
    safeBaseSubtotal + safeAdditionalCharges
  );
  const taxBase = subtotal;
  const tax = applyTax ? roundCurrency(taxBase * TAX_RATE) : 0;
  const finalEventTotal = roundCurrency(
    Math.max(0, subtotal + tax - safeDiscount)
  );

  return {
    baseSubtotal: safeBaseSubtotal,
    additionalCharges: safeAdditionalCharges,
    subtotal,
    taxBase,
    tax,
    discount: safeDiscount,
    finalEventTotal,
  };
};

// The invoice card fee applies to the unpaid balance, after tax, discount and
// deposit. Deposit confirmations themselves do not add another card fee.
export const calculateInvoiceBalance = (finalEventTotal, depositPaid, cardFeeEnabled = false) => {
  const balanceBeforeCard = Math.max(0, calculateRemainingBalance(finalEventTotal, depositPaid));
  const cardFee = cardFeeEnabled ? roundCurrency(balanceBeforeCard * CARD_FEE_RATE) : 0;
  return { balanceBeforeCard, cardFee, total: roundCurrency(balanceBeforeCard + cardFee) };
};

export const calculateRemainingBalance = (finalEventTotal, depositPaid) =>
  roundCurrency(
    toNonNegativeCurrency(finalEventTotal) -
      toNonNegativeCurrency(depositPaid)
  );
